import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  ArrowLeft, Save, Plus, Minus, X, Search, Truck, Package, 
  Building2, ShoppingCart, AlertTriangle, Calculator, Loader2 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api, { productsAPI } from '@/services/api';

const LocalPurchaseOrderPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  
  // Search states
  const [vendorSearch, setVendorSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    vendor_id: '',
    vendor_name: '',
    vendor_code: '',
    reference_number: '',
    expected_date: '',
    notes: '',
    items: []
  });

  // Fetch local vendors for this store
  const fetchVendors = useCallback(async () => {
    try {
      const response = await api.get('/vendors/store/local-vendors');
      setVendors(response.data || []);
      
      // If vendor_id is in URL, pre-select it
      const preselectedVendorId = searchParams.get('vendor_id');
      if (preselectedVendorId) {
        const vendor = (response.data || []).find(v => v.id === preselectedVendorId);
        if (vendor) {
          handleSelectVendor(vendor);
        }
      }
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
      toast({ title: 'Error', description: 'Failed to fetch local vendors', variant: 'destructive' });
    }
  }, [searchParams, toast]);

  // Fetch products that allow local purchase
  const fetchProducts = useCallback(async () => {
    try {
      const response = await productsAPI.list({ is_active: true, allow_local_purchase: true });
      setProducts(response.data?.items || response.data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVendors();
    fetchProducts();
  }, [fetchVendors, fetchProducts]);

  // Filter vendors
  const filteredVendors = vendors.filter(v => 
    !vendorSearch || 
    v.name.toLowerCase().includes(vendorSearch.toLowerCase()) ||
    v.code.toLowerCase().includes(vendorSearch.toLowerCase())
  ).slice(0, 10);

  // Filter products
  const filteredProducts = products.filter(p =>
    !productSearch ||
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.sku.toLowerCase().includes(productSearch.toLowerCase())
  ).slice(0, 10);

  const handleSelectVendor = (vendor) => {
    setSelectedVendor(vendor);
    setFormData(prev => ({
      ...prev,
      vendor_id: vendor.id,
      vendor_name: vendor.name,
      vendor_code: vendor.code
    }));
    setVendorSearch(vendor.name);
    setShowVendorDropdown(false);
  };

  const handleAddProduct = (product) => {
    const exists = formData.items.find(i => i.product_id === product.id);
    if (exists) {
      toast({ title: 'Info', description: 'Product already added' });
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        uom: product.uom_symbol || 'PCS',
        quantity: 1,
        unit_price: product.current_price?.cost_price || 0,
        tax_rate: product.tax_rate || 18,
        mrp: product.current_price?.mrp || 0
      }]
    }));
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const handleUpdateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const isNumeric = ['quantity', 'unit_price', 'tax_rate'].includes(field);
        return { ...item, [field]: isNumeric ? (parseFloat(value) || 0) : value };
      })
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Calculations
  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unit_price;
    const tax = subtotal * (item.tax_rate / 100);
    return subtotal + tax;
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let taxTotal = 0;
    let totalQty = 0;

    formData.items.forEach(item => {
      const lineSubtotal = item.quantity * item.unit_price;
      const lineTax = lineSubtotal * (item.tax_rate / 100);
      subtotal += lineSubtotal;
      taxTotal += lineTax;
      totalQty += item.quantity;
    });

    return {
      subtotal: subtotal.toFixed(2),
      taxTotal: taxTotal.toFixed(2),
      grandTotal: (subtotal + taxTotal).toFixed(2),
      totalQty,
      totalItems: formData.items.length
    };
  };

  const totals = calculateTotals();

  const handleSave = async (status = 'draft') => {
    if (!formData.vendor_id) {
      toast({ title: 'Validation Error', description: 'Please select a vendor', variant: 'destructive' });
      return;
    }
    if (formData.items.length === 0) {
      toast({ title: 'Validation Error', description: 'Please add at least one product', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        vendor_id: formData.vendor_id,
        reference_number: formData.reference_number,
        expected_delivery_date: formData.expected_date ? new Date(formData.expected_date).toISOString() : null,
        notes: formData.notes,
        status: status,
        items: formData.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_rate: item.tax_rate
        }))
      };

      await api.post('/store/local-purchase-orders', payload);
      toast({ title: 'Success', description: `Local Purchase Order ${status === 'submitted' ? 'submitted' : 'saved as draft'}` });
      navigate('/store/local-purchase');
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save purchase order', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/store/local-vendors')} className="text-gray-600">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-amber-600" /> New Local Purchase Order
          </h1>
          <p className="text-gray-500">Create a purchase order from local vendor</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vendor Selection */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-800 flex items-center gap-2">
                <Truck className="w-5 h-5 text-amber-600" /> Select Vendor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search local vendor..."
                  value={vendorSearch}
                  onChange={(e) => { setVendorSearch(e.target.value); setShowVendorDropdown(true); }}
                  onFocus={() => setShowVendorDropdown(true)}
                  className="pl-10 bg-gray-50 border-gray-300"
                />
                {showVendorDropdown && filteredVendors.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredVendors.map(vendor => (
                      <div
                        key={vendor.id}
                        onClick={() => handleSelectVendor(vendor)}
                        className="px-4 py-3 hover:bg-amber-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <p className="font-medium text-gray-800">{vendor.name}</p>
                        <p className="text-xs text-gray-500">{vendor.code} | {vendor.contact?.phone}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {selectedVendor && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-amber-800">{selectedVendor.name}</p>
                      <p className="text-sm text-amber-600">{selectedVendor.code}</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">
                      Balance: ₹{Math.abs(selectedVendor.current_balance || 0).toLocaleString()}
                    </Badge>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order Details */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-800">Order Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Reference Number</Label>
                  <Input
                    value={formData.reference_number}
                    onChange={(e) => setFormData(p => ({ ...p, reference_number: e.target.value }))}
                    placeholder="Optional reference"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Expected Delivery</Label>
                  <Input
                    type="date"
                    value={formData.expected_date}
                    onChange={(e) => setFormData(p => ({ ...p, expected_date: e.target.value }))}
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Any special instructions..."
                  className="bg-gray-50 border-gray-300"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Product Selection */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-800 flex items-center gap-2">
                <Package className="w-5 h-5 text-teal-600" /> Products
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Product Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search products (only local-purchase enabled)..."
                  value={productSearch}
                  onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                  onFocus={() => setShowProductDropdown(true)}
                  className="pl-10 bg-gray-50 border-gray-300"
                />
                {showProductDropdown && productSearch && filteredProducts.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredProducts.map(product => (
                      <div
                        key={product.id}
                        onClick={() => handleAddProduct(product)}
                        className="px-4 py-3 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <p className="font-medium text-gray-800">{product.name}</p>
                        <p className="text-xs text-gray-500">{product.sku} | MRP: ₹{product.current_price?.mrp || 0}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Items Table */}
              {formData.items.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-amber-600 text-white">
                        <TableHead className="text-white font-semibold">#</TableHead>
                        <TableHead className="text-white font-semibold">Product</TableHead>
                        <TableHead className="text-white font-semibold">Qty</TableHead>
                        <TableHead className="text-white font-semibold">Rate</TableHead>
                        <TableHead className="text-white font-semibold">Tax%</TableHead>
                        <TableHead className="text-white font-semibold text-right">Amount</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index} className="border-gray-200">
                          <TableCell className="text-gray-500">{index + 1}</TableCell>
                          <TableCell>
                            <p className="font-medium text-gray-800">{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.product_sku}</p>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleUpdateItem(index, 'quantity', e.target.value)}
                              className="bg-amber-50 border-amber-200 h-8 w-20"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => handleUpdateItem(index, 'unit_price', e.target.value)}
                              className="bg-gray-50 border-gray-300 h-8 w-24"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={item.tax_rate}
                              onChange={(e) => handleUpdateItem(index, 'tax_rate', e.target.value)}
                              className="bg-gray-50 border-gray-300 h-8 w-16"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium text-gray-800">
                            ₹{calculateItemTotal(item).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRemoveItem(index)}>
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                  <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">Search and add products above</p>
                  <p className="text-xs text-gray-400 mt-1">Only products with "Allow Local Purchase" enabled will appear</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Summary Panel */}
        <div className="space-y-6">
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 shadow-sm sticky top-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-amber-800 flex items-center gap-2">
                <Calculator className="w-5 h-5" /> Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Items:</span>
                  <span className="font-medium">{totals.totalItems}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Qty:</span>
                  <span className="font-medium">{totals.totalQty}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">₹{totals.subtotal}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax:</span>
                  <span className="font-medium">₹{totals.taxTotal}</span>
                </div>
                <hr className="border-amber-300" />
                <div className="flex justify-between text-lg">
                  <span className="font-bold text-amber-800">Total:</span>
                  <span className="font-bold text-teal-600">₹{totals.grandTotal}</span>
                </div>
              </div>

              <div className="space-y-2 pt-4">
                <Button 
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => handleSave('submitted')}
                  disabled={saving || formData.items.length === 0}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Submit PO
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => handleSave('draft')}
                  disabled={saving || formData.items.length === 0}
                >
                  Save as Draft
                </Button>
              </div>

              <div className="p-3 rounded-lg bg-white/50 border border-amber-200 mt-4">
                <p className="text-xs text-amber-700">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Payment for this order will be managed by HQ
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LocalPurchaseOrderPage;
